import { useState } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Trash } from "@phosphor-icons/react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  buildDimensionFormDefaults,
  flattenDimensionItems,
  renderDimensionFormCells,
} from "../Categories/components/Category/helpers";
import { useDimensionFiltering } from "../Categories/components/Category/useDimensionFiltering";
import { ONSITE_CERTIFICATE_TYPES, ONSITE_FUEL_TYPES } from "./const";
import { LIME_FOCUS, mapOnsiteFormDataToApiData } from "./helpers";
import { DECIMAL_STEP, exceedsMaxDecimals, MAX_DECIMAL_PLACES } from "../../../../utils/decimals";
import { renderDimensionHeaderCells, activeDimensionCount } from "./dimensionDisplay";

const FIELD_ARRAY_NAME = "onsiteGeneration";

const TH_PROPS = {
  py: 3,
  px: 4,
  fontSize: "13px",
  color: "gray.600",
  fontWeight: "600",
  textTransform: "none",
  letterSpacing: "normal",
};

const NumberCell = ({ register, name, isInvalid }) => (
  <Input
    size="sm"
    type="number"
    step={DECIMAL_STEP}
    borderRadius="md"
    placeholder="kWh"
    onWheel={(e) => e.target.blur()}
    borderColor={isInvalid ? "red.400" : undefined}
    {...LIME_FOCUS}
    {...register(name)}
  />
);

// Every numeric field on an on-site row; each is held to MAX_DECIMAL_PLACES.
const ONSITE_NUMBER_FIELDS = [
  "totalGenerated",
  "selfConsumed",
  "exportedToGrid",
  "certificatesRetained",
  "certificatesTransferred",
];

/**
 * RHF modal for the wide on-site generation schema (dims + Fuel/Tech + Total
 * Generated + Self Consumed + Exported + Certificates Issued + Certificate
 * Type + Retained + Transferred). Certificate Type/Retained/Transferred are
 * only enabled when issued; the underlying DTO converts the Yes/No select to a
 * boolean and nulls the dependent fields when not issued.
 */
function OnsiteGenerationModal({
  isOpen,
  onClose,
  onSubmitOnsite,
  organizationStructure,
  dimensionItems,
  editData,
}) {
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire.scope2V2",
  });

  const isEdit = !!editData;
  const dimCount = activeDimensionCount(organizationStructure);

  const buildEmptyRow = () => ({
    ...buildDimensionFormDefaults(organizationStructure, dimensionItems),
    fuelType: "",
    totalGenerated: "",
    selfConsumed: "",
    exportedToGrid: "",
    certificatesIssued: "no",
    certificateType: "",
    certificatesRetained: "",
    certificatesTransferred: "",
  });

  const seededEditData = editData
    ? {
        ...flattenDimensionItems({ ...editData }),
        fuelType: editData.fuelType ?? "",
        totalGenerated: editData.totalGenerated ?? "",
        selfConsumed: editData.selfConsumed ?? "",
        exportedToGrid: editData.exportedToGrid ?? "",
        certificatesIssued: editData.certificatesIssued ? "yes" : "no",
        certificateType: editData.certificateType ?? "",
        certificatesRetained: editData.certificatesRetained ?? "",
        certificatesTransferred: editData.certificatesTransferred ?? "",
      }
    : null;

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      [FIELD_ARRAY_NAME]: [seededEditData ? seededEditData : buildEmptyRow()],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: FIELD_ARRAY_NAME });
  const watchFieldArray = watch(FIELD_ARRAY_NAME);

  const { getFilteredItems, setChangedRowIndex } = useDimensionFiltering({
    dimensionItems,
    editData: seededEditData,
    watchFieldArray,
  });

  const [showErrors, setShowErrors] = useState(false);

  // Toggling "Certificates Issued" to "no" clears the dependent certificate
  // fields so stale values can't leak back when toggled on again.
  const handleIssuedChange = (index, value) => {
    if (value !== "yes") {
      setValue(`${FIELD_ARRAY_NAME}.${index}.certificateType`, "");
      setValue(`${FIELD_ARRAY_NAME}.${index}.certificatesRetained`, "");
      setValue(`${FIELD_ARRAY_NAME}.${index}.certificatesTransferred`, "");
    }
  };

  const isRowValid = (row) =>
    !!row.fuelType &&
    row.totalGenerated !== "" &&
    row.totalGenerated !== null &&
    row.totalGenerated !== undefined &&
    (row.certificatesIssued !== "yes" || !!row.certificateType) &&
    ONSITE_NUMBER_FIELDS.every((field) => !exceedsMaxDecimals(row[field]));

  const submit = (formValues) => {
    const rows = formValues[FIELD_ARRAY_NAME];
    const hasBlockingRow = rows.some((row) => !isRowValid(row));
    if (hasBlockingRow) {
      setShowErrors(true);
      return;
    }
    onSubmitOnsite(mapOnsiteFormDataToApiData(rows), isEdit);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent
        borderRadius="lg"
        w="fit-content"
        maxW="calc(100vw - 32px)"
        minW={{ base: "auto", md: "640px" }}
      >
        <ModalHeader fontSize="20px" fontWeight="700" color="darkTeal">
          {t("addOnsite")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflowX="auto">
            <Table size="sm" variant="unstyled">
              <Thead>
                <Tr bg="gray.50">
                  {renderDimensionHeaderCells(organizationStructure)}
                  <Th {...TH_PROPS}>{t("columns.fuelType")}</Th>
                  <Th {...TH_PROPS}>{t("columns.totalGenerated")}</Th>
                  <Th {...TH_PROPS}>{t("columns.selfConsumed")}</Th>
                  <Th {...TH_PROPS}>{t("columns.exportedToGrid")}</Th>
                  <Th {...TH_PROPS}>{t("columns.certificatesIssued")}</Th>
                  <Th {...TH_PROPS}>{t("columns.certificateType")}</Th>
                  <Th {...TH_PROPS}>{t("columns.certificatesRetained")}</Th>
                  <Th {...TH_PROPS}>{t("columns.certificatesTransferred")}</Th>
                  <Th py={3} px={4} width="48px" />
                </Tr>
              </Thead>
              <Tbody>
                {fields.map((field, index) => {
                  const row = watchFieldArray[index] || {};
                  const issued = row.certificatesIssued === "yes";
                  const fuelMissing = showErrors && !row.fuelType;
                  const totalMissing =
                    showErrors &&
                    (row.totalGenerated === "" || row.totalGenerated === null || row.totalGenerated === undefined);
                  const certTypeMissing = showErrors && issued && !row.certificateType;
                  const tooPrecise = (field) => showErrors && exceedsMaxDecimals(row[field]);

                  const dimCells = renderDimensionFormCells({
                    organizationStructure,
                    dimensionItems,
                    register,
                    fieldArrayName: FIELD_ARRAY_NAME,
                    index,
                    watchFieldArray,
                    getFilteredItems,
                    onDimensionChange: setChangedRowIndex,
                  });

                  const issuedSelector = register(`${FIELD_ARRAY_NAME}.${index}.certificatesIssued`);

                  return (
                    <Tr key={field.id} borderTop={index === 0 ? "none" : "1px solid"} borderColor="gray.100">
                      {dimCells.map((cell, dimIdx) => (
                        <Td key={`dim-${field.id}-${dimIdx}`} py={3} px={4}>
                          {cell}
                        </Td>
                      ))}
                      <Td py={3} px={4}>
                        <Box>
                          <Select
                            size="sm"
                            borderRadius="md"
                            placeholder=" "
                            borderColor={fuelMissing ? "red.400" : undefined}
                            {...LIME_FOCUS}
                            {...register(`${FIELD_ARRAY_NAME}.${index}.fuelType`)}
                          >
                            {ONSITE_FUEL_TYPES.map((code) => (
                              <option key={code} value={code}>{t(`fuelTypes.${code}`)}</option>
                            ))}
                          </Select>
                          {fuelMissing && (
                            <Text fontSize="11px" color="red.500" mt={1}>{t("required")}</Text>
                          )}
                        </Box>
                      </Td>
                      <Td py={3} px={4}>
                        <Box>
                          <Input
                            size="sm"
                            type="number"
                            step={DECIMAL_STEP}
                            borderRadius="md"
                            placeholder="kWh"
                            onWheel={(e) => e.target.blur()}
                            borderColor={totalMissing || tooPrecise("totalGenerated") ? "red.400" : undefined}
                            {...LIME_FOCUS}
                            {...register(`${FIELD_ARRAY_NAME}.${index}.totalGenerated`)}
                          />
                          {totalMissing && (
                            <Text fontSize="11px" color="red.500" mt={1}>{t("required")}</Text>
                          )}
                          {tooPrecise("totalGenerated") && (
                            <Text fontSize="11px" color="red.500" mt={1}>
                              {t("maxDecimals", { count: MAX_DECIMAL_PLACES })}
                            </Text>
                          )}
                        </Box>
                      </Td>
                      <Td py={3} px={4}>
                        <NumberCell
                          register={register}
                          name={`${FIELD_ARRAY_NAME}.${index}.selfConsumed`}
                          isInvalid={tooPrecise("selfConsumed")}
                        />
                      </Td>
                      <Td py={3} px={4}>
                        <NumberCell
                          register={register}
                          name={`${FIELD_ARRAY_NAME}.${index}.exportedToGrid`}
                          isInvalid={tooPrecise("exportedToGrid")}
                        />
                      </Td>
                      <Td py={3} px={4}>
                        <Select
                          size="sm"
                          borderRadius="md"
                          {...LIME_FOCUS}
                          {...issuedSelector}
                          onChange={(e) => {
                            issuedSelector.onChange(e);
                            handleIssuedChange(index, e.target.value);
                          }}
                        >
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </Select>
                      </Td>
                      <Td py={3} px={4}>
                        {issued ? (
                          <Box>
                            <Select
                              size="sm"
                              borderRadius="md"
                              placeholder={t("columns.certificateType")}
                              borderColor={certTypeMissing ? "red.400" : undefined}
                              {...LIME_FOCUS}
                              {...register(`${FIELD_ARRAY_NAME}.${index}.certificateType`)}
                            >
                              {ONSITE_CERTIFICATE_TYPES.map((code) => (
                                <option key={code} value={code}>{t(`certificateTypes.${code}`)}</option>
                              ))}
                            </Select>
                            {certTypeMissing && (
                              <Text fontSize="11px" color="red.500" mt={1}>{t("required")}</Text>
                            )}
                          </Box>
                        ) : (
                          <Text fontSize="14px" color="gray.400">—</Text>
                        )}
                      </Td>
                      <Td py={3} px={4}>
                        {issued ? (
                          <NumberCell
                          register={register}
                          name={`${FIELD_ARRAY_NAME}.${index}.certificatesRetained`}
                          isInvalid={tooPrecise("certificatesRetained")}
                        />
                        ) : (
                          <Text fontSize="14px" color="gray.400">—</Text>
                        )}
                      </Td>
                      <Td py={3} px={4}>
                        {issued ? (
                          <NumberCell
                          register={register}
                          name={`${FIELD_ARRAY_NAME}.${index}.certificatesTransferred`}
                          isInvalid={tooPrecise("certificatesTransferred")}
                        />
                        ) : (
                          <Text fontSize="14px" color="gray.400">—</Text>
                        )}
                      </Td>
                      <Td py={3} px={4} textAlign="right">
                        <IconButton
                          aria-label="Remove draft row"
                          icon={<Trash size={14} weight="bold" />}
                          size="sm"
                          variant="ghost"
                          color="gray.500"
                          isDisabled={fields.length <= 1}
                          _hover={{ color: "red.500", bg: "red.50" }}
                          onClick={() => remove(index)}
                        />
                      </Td>
                    </Tr>
                  );
                })}
                {!isEdit && (
                  <Tr bg="lightLime">
                    <Td colSpan={dimCount + 9} py={3} px={4}>
                      <Text
                        as="button"
                        type="button"
                        fontSize="14px"
                        fontWeight="600"
                        color="limeShade.700"
                        onClick={() => append(buildEmptyRow())}
                      >
                        {t("addAnother")}
                      </Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Box>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button
            bg="lime"
            color="pureBlack"
            borderRadius="full"
            _hover={{ bg: "limeShade.300" }}
            onClick={handleSubmit(submit)}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default OnsiteGenerationModal;
